"use client";

import { Check, Code2, Link2, MoreHorizontal, Share2 } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { toast } from "sonner";
import { SnippetField } from "@/components/embed/snippet-field";
import { useLocale } from "@/components/locale-link";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { writeToClipboard } from "@/lib/clipboard";
import {
  buildIframeSnippet,
  defaultEmbedAppearance,
  type EmbedAppearance,
  type EmbedChrome,
  type EmbedTarget,
  type EmbedTheme,
  embedStudioHref,
  embedWidgetHref,
} from "@/lib/embed-config";
import { EMBED_COPY } from "@/lib/embed-copy";
import { withLocale } from "@/lib/i18n/link-path";

export function PageShareMenu({
  target,
  pageTitle,
  pagePath,
}: {
  target: EmbedTarget;
  pageTitle: string;
  /** Unprefixed canonical path of the host page. */
  pagePath: string;
}) {
  const locale = useLocale();
  const [menuOpen, setMenuOpen] = useState(false);
  const [studioOpen, setStudioOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [theme, setTheme] = useState<EmbedTheme>("system");
  const [chrome, setChrome] = useState<EmbedChrome>("branded");
  const [height, setHeight] = useState(720);

  const pageUrl =
    typeof window === "undefined"
      ? withLocale(pagePath, locale)
      : `${window.location.origin}${withLocale(pagePath, locale)}`;

  const appearance: EmbedAppearance = useMemo(
    () => ({
      ...defaultEmbedAppearance(),
      theme,
      chrome,
      height,
    }),
    [theme, chrome, height]
  );

  const origin =
    typeof window === "undefined" ? undefined : window.location.origin;
  const snippet = buildIframeSnippet(
    { locale, target, appearance },
    { title: pageTitle, origin, pagePath }
  );
  const studioHref = embedStudioHref({ locale, target }, origin);
  const widgetHref = embedWidgetHref({ locale, target, appearance }, origin);

  async function copyLink() {
    if (await writeToClipboard(pageUrl)) {
      setCopiedLink(true);
      window.setTimeout(() => {
        setCopiedLink(false);
      }, 2000);
      return;
    }
    toast.error(EMBED_COPY.studio.copyFailed);
  }

  async function copyDefaultEmbed() {
    if (await writeToClipboard(snippet)) {
      toast.success(EMBED_COPY.menu.embedCopied);
      setMenuOpen(false);
      return;
    }
    toast.error(EMBED_COPY.studio.copyFailed);
  }

  return (
    <div className="print:hidden">
      <Popover onOpenChange={setMenuOpen} open={menuOpen}>
        <PopoverTrigger asChild>
          <Button
            aria-label={EMBED_COPY.menu.shareMenu}
            className="gap-1.5"
            size="sm"
            type="button"
            variant="outline"
          >
            <Share2 className="size-3.5" />
            <span className="hidden sm:inline">{EMBED_COPY.menu.share}</span>
            <MoreHorizontal className="size-3.5 sm:hidden" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 space-y-1 p-2">
          <p className="px-2 py-1.5 font-medium text-muted-foreground text-xs">
            {EMBED_COPY.menu.shareMenu}
          </p>
          <MenuButton
            icon={
              copiedLink ? (
                <Check className="size-3.5" />
              ) : (
                <Link2 className="size-3.5" />
              )
            }
            label={
              copiedLink ? EMBED_COPY.menu.linkCopied : EMBED_COPY.menu.copyLink
            }
            onClick={copyLink}
          />
          <MenuButton
            icon={<Code2 className="size-3.5" />}
            label={EMBED_COPY.menu.embed}
            onClick={() => {
              setMenuOpen(false);
              setStudioOpen(true);
            }}
          />
          <MenuButton
            icon={<Code2 className="size-3.5" />}
            label={EMBED_COPY.menu.copyEmbed}
            onClick={copyDefaultEmbed}
          />
          <a
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-accent"
            href={studioHref}
          >
            {EMBED_COPY.menu.openStudio}
          </a>
        </PopoverContent>
      </Popover>

      <Dialog onOpenChange={setStudioOpen} open={studioOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{EMBED_COPY.menu.embed}</DialogTitle>
            <DialogDescription>
              {EMBED_COPY.menu.embedDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={EMBED_COPY.studio.themeLabel}>
              <Select
                onValueChange={(value) => {
                  setTheme(value as EmbedTheme);
                }}
                value={theme}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system">
                    {EMBED_COPY.studio.themeSystem}
                  </SelectItem>
                  <SelectItem value="light">
                    {EMBED_COPY.studio.themeLight}
                  </SelectItem>
                  <SelectItem value="dark">
                    {EMBED_COPY.studio.themeDark}
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={EMBED_COPY.studio.chromeLabel}>
              <Select
                onValueChange={(value) => {
                  setChrome(value as EmbedChrome);
                }}
                value={chrome}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="branded">
                    {EMBED_COPY.studio.chromeBranded}
                  </SelectItem>
                  <SelectItem value="minimal">
                    {EMBED_COPY.studio.chromeMinimal}
                  </SelectItem>
                  <SelectItem value="bare">
                    {EMBED_COPY.studio.chromeBare}
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={EMBED_COPY.studio.heightLabel}>
              <input
                className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                max={2400}
                min={360}
                onChange={(event) => {
                  setHeight(Number(event.target.value) || 720);
                }}
                type="number"
                value={height}
              />
            </Field>
          </div>
          <SnippetField
            id="page-embed-snippet"
            label={EMBED_COPY.studio.htmlLabel}
            rows={5}
            value={snippet}
          />
          <p className="text-muted-foreground text-xs leading-relaxed">
            Preview:{" "}
            <a
              className="underline underline-offset-4"
              href={widgetHref}
              rel="noreferrer"
              target="_blank"
            >
              {EMBED_COPY.studio.openFullPage}
            </a>
            {" · "}
            <a className="underline underline-offset-4" href={studioHref}>
              {EMBED_COPY.menu.openStudio}
            </a>
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MenuButton({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-accent"
      onClick={onClick}
      type="button"
    >
      {icon}
      {label}
    </button>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
